import { useCallback, useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Property } from "../../data/models";
import PropertyCard from "../../components/valuation/PropertyCard";
import StatsCard from "../../components/StatsCard";
import ProtectedRoute from "../../components/ProtectedRoute";
import { AuthProvider } from "../../context/AuthContext";
import LineChartComponent from "../../components/charts/LineChart";
import PieChartComponent from "../../components/charts/PieChart";
import Header from "@/components/supervisor/Header";
import Sidebar from "@/components/supervisor/Sidebar";
import { useNavigate } from "react-router-dom";
import { USER_ROLES } from "../../data/models";
import DataCollectorsPerformance from "@/components/supervisor/DataCollectorsPerformance";

// Define interface for stat object
interface Stat {
  title: string;
  value: string;
  change: string;
  trend: "up" | "down" | "neutral";
  icon: {
    path: string;
    bgColor: string;
  };
}

interface ClientStats {
  total: number;
  pending: number;
  completed: number;
  inProgress: number;
}

// Define interface for chart data
interface LineChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    fill: boolean;
    borderColor: string;
    tension: number;
  }[];
}

interface PieChartData {
  labels: string[];
  datasets: {
    data: number[];
    backgroundColor: string[];
  }[];
}

// Define interface for user
interface User {
  _id: string;
  fullName: string;
  email: string;
  role: "SUPERVISOR" | "DATA_COLLECTOR" | "ADMIN";
  employeeId?: string;
  createdAt: string;
}

interface CollectorStats {
  assigned: number;
  pending: number;
  completed: number;
  inProgress: number;
}

function useTheme() {
  const [theme, setThemeState] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const storedTheme =
      (localStorage.getItem("theme") as "light" | "dark") || "light";
    setThemeState(storedTheme);
    setMounted(true);
  }, []);

  const setTheme = useCallback((newTheme: "light" | "dark") => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);
  }, []);

  useEffect(() => {
    if (mounted) {
      document.documentElement.classList.toggle("dark", theme === "dark");
    }
  }, [theme, mounted]);

  return { theme, setTheme, mounted };
}

const SupervisorDashboardContent = () => {
  const { currentUser, logout, token: authToken } = useAuth();
  const { theme, setTheme } = useTheme();
  const [filter, setFilter] = useState<"all" | "pending" | "completed" | "in-progress">("all");
  const [clientStats, setClientStats] = useState<Record<string, ClientStats>>({});
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [dataCollectors, setDataCollectors] = useState<User[]>([]);
  const [stats, setStats] = useState<Stat[]>([]);
  const [lineChartData, setLineChartData] = useState<LineChartData>({
    labels: [],
    datasets: [
      {
        label: "Property Valuations",
        data: [],
        fill: false,
        borderColor: "rgb(79, 70, 229)",
        tension: 0.1,
      },
    ],
  });
  const [pieChartData, setPieChartData] = useState<PieChartData>({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
      },
    ],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collectorStats, setCollectorStats] = useState<Record<string, CollectorStats>>({});
  const [collectorNameMap, setCollectorNameMap] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  // Store original fetch function
  const originalFetch = window.fetch;

  // Intercept fetch requests to handle 401 responses
  useEffect(() => {
    const fetchInterceptor = async (...args: Parameters<typeof fetch>) => {
      try {
        const response = await originalFetch(...args);

        // Clone response to read it without consuming the stream
        const clonedResponse = response.clone();

        // Check for unauthorized response
        if (response.status === 401) {
          console.log("Token expired, logging out...");
          localStorage.removeItem("valuation_token");
          localStorage.removeItem("valuation_user");
          navigate("/login", {
            replace: true,
            state: {
              message: "Your session has expired. Please login again.",
              sessionExpired: true,
            },
          });
          return response;
        }

        return response;
      } catch (error) {
        console.error("Fetch error:", error);
        throw error;
      }
    };

    window.fetch = fetchInterceptor as typeof fetch;
    return () => {
      window.fetch = originalFetch;
    };
  }, [navigate]);

  const handleAction = (action: string) => {
    switch (action) {
      case "view-report":
        alert("Viewing report...");
        break;
      case "view-data":
        alert("Navigating to Data...");
        break;
      default:
        break;
    }
  };

  const apiUrl = import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";

  // Fetch properties from API
 useEffect(() => {
  const fetchProperties = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("valuation_token");
      if (!token) {
        throw new Error("No authentication token found. Please log in again.");
      }

      let allProperties: any[] = [];
      let page = 1;
      let hasNextPage = true;

      while (hasNextPage) {
        const response = await fetch(`${apiUrl}/properties?page=${page}&limit=20`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("Unauthorized: Invalid or expired token. Please log in again.");
          }
          throw new Error(`Failed to fetch properties: ${response.statusText}`);
        }

        const data: any = await response.json();
        allProperties = [...allProperties, ...(data.data || [])];
        hasNextPage = data.meta?.hasNextPage ?? false;
        page++;
      }

      console.log("Fetched properties:", allProperties);
      setProperties(allProperties);
      setFilteredProperties(allProperties);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while fetching properties"
      );
    } finally {
      setLoading(false);
    }
  };

  fetchProperties();
}, []);

  // Helper function to normalize property status
  const normalizeStatus = (status: string | undefined): string => {
    if (!status) return "in-progress";
    
    const statusLower = status.toLowerCase();
    
    if (statusLower === "draft" || statusLower === "new" || statusLower === "in_progress") {
      return "in-progress";
    }
    
    if (statusLower === "pending") {
      return "pending";
    }
    
    if (statusLower === "completed" || statusLower === "approved" || statusLower === "certified") {
      return "completed";
    }
    
    // Default to in-progress for any other status
    return "in-progress";
  };

  // Filter properties based on filter state
  useEffect(() => {
    if (properties.length === 0) return;

    let filtered = [...properties];
    
    switch (filter) {
      case "pending":
        filtered = properties.filter(p => 
          normalizeStatus(p.status) === "pending"
        );
        break;
      case "completed":
        filtered = properties.filter(p => 
          normalizeStatus(p.status) === "completed"
        );
        break;
      case "in-progress":
        filtered = properties.filter(p => 
          normalizeStatus(p.status) === "in-progress"
        );
        break;
      case "all":
      default:
        filtered = [...properties];
        break;
    }
    
    setFilteredProperties(filtered);
  }, [filter, properties]);

  // Calculate stats when properties change
  useEffect(() => {
    if (properties.length === 0) return;

    const totalProperties = properties.length;
    
    // Count properties by normalized status
    const pendingProperties = properties.filter(p => 
      normalizeStatus(p.status) === "pending"
    ).length;
    
    const completedProperties = properties.filter(p => 
      normalizeStatus(p.status) === "completed"
    ).length;
    
    const inProgressProperties = properties.filter(p => 
      normalizeStatus(p.status) === "in-progress"
    ).length;

    // Calculate average property value
    const totalValue = properties.reduce((sum: number, p: Property) => {
      const value = p.valuationDetails?.totalPropertyValue || p.totalPropertyValue || 0;
      return sum + Number(value);
    }, 0);
    
    const averageValue = totalProperties > 0 ? totalValue / totalProperties : 0;

    // Calculate percentage changes
    const pendingPercentage = totalProperties > 0 ? (pendingProperties / totalProperties * 100).toFixed(0) : "0";
    const completedPercentage = totalProperties > 0 ? (completedProperties / totalProperties * 100).toFixed(0) : "0";
    const inProgressPercentage = totalProperties > 0 ? (inProgressProperties / totalProperties * 100).toFixed(0) : "0";

    setStats([
      {
        title: "Total Properties",
        value: totalProperties.toString(),
        change: "All properties in system",
        trend: "neutral",
        icon: {
          path: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
          bgColor: "bg-blue-500",
        },
      },
      {
        title: "Pending Approval",
        value: pendingProperties.toString(),
        change: `${pendingPercentage}% of total`,
        trend: pendingProperties > 0 ? "up" : "neutral",
        icon: {
          path: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
          bgColor: "bg-yellow-500",
        },
      },
      {
        title: "In Progress",
        value: inProgressProperties.toString(),
        change: `${inProgressPercentage}% of total`,
        trend: inProgressProperties > 0 ? "up" : "neutral",
        icon: {
          path: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
          bgColor: "bg-orange-500",
        },
      },
      {
        title: "Completed",
        value: completedProperties.toString(),
        change: `${completedPercentage}% of total`,
        trend: completedProperties > 0 ? "up" : "neutral",
        icon: {
          path: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
          bgColor: "bg-green-500",
        },
      },
    ]);

    // Update charts
    const monthlyData = calculateMonthlyValuations(properties);
    setLineChartData(monthlyData);

    const propertyTypeData = calculatePropertyTypeDistribution(properties);
    setPieChartData(propertyTypeData);

  }, [properties]);

  useEffect(() => {
    fetchDataCollectors();
  }, []);

  useEffect(() => {
    if (properties.length > 0 && dataCollectors.length > 0) {
      calculateCollectorStats();
      createCollectorNameMap();
      calculateClientStats();
    }
  }, [properties, dataCollectors]);

  const calculateClientStats = () => {
    const stats: Record<string, ClientStats> = {};

    properties.forEach((property) => {
      const clientKey = property.viewerOwnerId || property.ownerIdNumber || property.ownerName;
      if (!clientKey) return;

      if (!stats[clientKey]) {
        stats[clientKey] = {
          total: 0,
          pending: 0,
          completed: 0,
          inProgress: 0,
        };
      }

      stats[clientKey].total++;

      const normalizedStatus = normalizeStatus(property.status);
      if (normalizedStatus === "pending") {
        stats[clientKey].pending++;
      } else if (normalizedStatus === "completed") {
        stats[clientKey].completed++;
      } else if (normalizedStatus === "in-progress") {
        stats[clientKey].inProgress++;
      }
    });

    setClientStats(stats);
  };

  const fetchDataCollectors = async () => {
    try {
      const token = localStorage.getItem("valuation_token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(`${apiUrl}/users`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) return;
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const collectors = data.data.filter(
        (user: User) => user.role === "DATA_COLLECTOR"
      );
      setDataCollectors(collectors);
    } catch (err) {
      console.error("Error fetching data collectors:", err);
      setDataCollectors([]);
    }
  };

  const createCollectorNameMap = () => {
    const map: Record<string, string> = {};
    dataCollectors.forEach((collector) => {
      map[collector._id] = collector.fullName;
    });
    setCollectorNameMap(map);
  };

  const calculateCollectorStats = () => {
    const stats: Record<string, CollectorStats> = {};

    dataCollectors.forEach((collector) => {
      stats[collector._id] = {
        assigned: 0,
        pending: 0,
        completed: 0,
        inProgress: 0,
      };
    });

    properties.forEach((property) => {
      const collectorId = property.collectorId;
      if (collectorId && stats[collectorId]) {
        stats[collectorId].assigned++;

        const normalizedStatus = normalizeStatus(property.status);
        if (normalizedStatus === "pending") {
          stats[collectorId].pending++;
        } else if (normalizedStatus === "completed") {
          stats[collectorId].completed++;
        } else if (normalizedStatus === "in-progress") {
          stats[collectorId].inProgress++;
        }
      }
    });

    setCollectorStats(stats);
  };

  // Helper function to calculate monthly valuations
  const calculateMonthlyValuations = (properties: Property[]): LineChartData => {
    const monthlyCounts: Record<string, number> = {};
    const monthlyValues: Record<string, number> = {};

    properties.forEach((property) => {
      if (property.createdAt) {
        const date = new Date(property.createdAt);
        const monthYear = date.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });

        if (!monthlyCounts[monthYear]) {
          monthlyCounts[monthYear] = 0;
          monthlyValues[monthYear] = 0;
        }
        monthlyCounts[monthYear]++;
        
        // Add property value to monthly total
        const value = property.valuationDetails?.totalPropertyValue || property.totalPropertyValue || 0;
        monthlyValues[monthYear] += Number(value);
      }
    });

    const sortedMonths = Object.keys(monthlyCounts).sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });

    return {
      labels: sortedMonths,
      datasets: [
        {
          label: "Number of Properties",
          data: sortedMonths.map(month => monthlyCounts[month]),
          fill: false,
          borderColor: "rgb(79, 70, 229)",
          tension: 0.1,
        },
        {
          label: "Total Value (RWF)",
          data: sortedMonths.map(month => monthlyValues[month]),
          fill: false,
          borderColor: "rgb(16, 185, 129)",
          tension: 0.1,
        }
      ],
    };
  };

  // Helper function to calculate property type distribution
  const calculatePropertyTypeDistribution = (properties: Property[]): PieChartData => {
    const typeCounts: Record<string, number> = {};

    properties.forEach((property) => {
      const type = property.landUse || property.landParcel?.landUse || "Unknown";
      if (!typeCounts[type]) {
        typeCounts[type] = 0;
      }
      typeCounts[type]++;
    });

    const labels = Object.keys(typeCounts);
    const data = labels.map(label => typeCounts[label]);

    const colors = [
      "rgba(79, 70, 229, 0.8)",
      "rgba(59, 130, 246, 0.8)",
      "rgba(16, 185, 129, 0.8)",
      "rgba(245, 158, 11, 0.8)",
      "rgba(239, 68, 68, 0.8)",
      "rgba(139, 92, 246, 0.8)",
      "rgba(236, 72, 153, 0.8)",
      "rgba(20, 184, 166, 0.8)",
    ];

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors.slice(0, labels.length),
        },
      ],
    };
  };

  // Helper function to get collector name for a property
  const getCollectorName = (property: Property): string => {
    if (property.collectorId && collectorNameMap[property.collectorId]) {
      return collectorNameMap[property.collectorId];
    }
    if (property.valuationDetails?.assignedCollector) {
      return property.valuationDetails.assignedCollector;
    }
    if (property.assignedCollector) {
      return property.assignedCollector;
    }
    return "Unassigned";
  };

  const onPropertySelect = (propertyId: string) => {
    navigate(`/supervisor-dashboard/view-data/property/${propertyId}`);
  };

  // Handle logout when token expires
  const handleLogout = () => {
    localStorage.removeItem("valuation_token");
    localStorage.removeItem("valuation_user");
    logout();
    navigate("/login", {
      replace: true,
      state: {
        message: "Your session has expired. Please login again.",
        sessionExpired: true,
      },
    });
  };

  // Check token on component mount
  useEffect(() => {
    const token = localStorage.getItem("valuation_token");
    if (!token) {
      handleLogout();
    }
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <Header theme={theme} setTheme={setTheme} logout={handleLogout} />
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading dashboard
            </h3>
            <p className="text-sm text-red-700 mt-2">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-white">
      <Header theme={theme} setTheme={setTheme} logout={handleLogout} />
      <div className="flex">
        <Sidebar
          onViewData={() => handleAction("view-data")}
          onViewReport={() => handleAction("view-report")}
        />
        <main className="flex-1 lg:ml-72 md:ml lg:mr-5 p-6">
          <div className="space-y-6">
            <h1 className="text-2xl mt-5 font-semibold text-gray-800 dark:text-white">
              Dashboard
            </h1>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <StatsCard
                  key={index}
                  title={stat.title}
                  value={stat.value}
                  change={stat.change}
                  trend={stat.trend}
                  icon={stat.icon}
                />
              ))}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
                <h3 className="text-base font-medium text-gray-800 dark:text-white mb-4">
                  Property Valuations Over Time
                </h3>
                <div className="h-64">
                  <LineChartComponent data={lineChartData} />
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
                <h3 className="text-base font-medium text-gray-800 dark:text-white mb-4">
                  Property Types Distribution
                </h3>
                <div className="h-64">
                  <PieChartComponent data={pieChartData} />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mt-6">
              <div className="ml-auto flex bg-white dark:bg-gray-800 rounded-md shadow-sm">
                <button
                  onClick={() => setFilter("all")}
                  className={`px-4 py-2 text-sm font-medium rounded-l-md ${
                    filter === "all"
                      ? "bg-indigo-50 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilter("pending")}
                  className={`px-4 py-2 text-sm font-medium ${
                    filter === "pending"
                      ? "bg-indigo-50 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  Pending
                </button>
                <button
                  onClick={() => setFilter("in-progress")}
                  className={`px-4 py-2 text-sm font-medium ${
                    filter === "in-progress"
                      ? "bg-indigo-50 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  In Progress
                </button>
                <button
                  onClick={() => setFilter("completed")}
                  className={`px-4 py-2 text-sm font-medium rounded-r-md ${
                    filter === "completed"
                      ? "bg-indigo-50 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  Completed
                </button>
              </div>
            </div>

            {/* Properties Grid */}
            <div className="mt-6">
              <h2 className="text-lg font-medium text-gray-800 dark:text-white mb-4">
                {filter === "all"
                  ? "All Properties"
                  : filter === "pending"
                  ? "Pending Approval"
                  : filter === "in-progress"
                  ? "In Progress"
                  : "Completed Properties"} ({filteredProperties.length})
              </h2>

              {filteredProperties.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm text-center">
                  <p className="text-gray-500 dark:text-gray-400">
                    No properties found in this category.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProperties.map((property) => (
                    <PropertyCard
                      key={property.id}
                      property={property}
                      onClick={() => onPropertySelect(property.id)}
                      userRole={currentUser?.role ?? "SUPERVISOR"}
                      assignedToName={getCollectorName(property)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Data Collectors Performance */}
            <div className="mt-8">
              <h2 className="text-lg font-medium text-gray-800 dark:text-white mb-4">
                Data Collectors Performance
              </h2>
              <DataCollectorsPerformance
                dataCollectors={dataCollectors}
                properties={properties}
                collectorStats={collectorStats}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default function SupervisorDashboard() {
  return (
    <AuthProvider>
      <ProtectedRoute allowedRoles={["SUPERVISOR"]}>
        <SupervisorDashboardContent />
      </ProtectedRoute>
    </AuthProvider>
  );
}